import React, { useState, useEffect, useContext } from "react";
import Papa from "papaparse";
import {
  doc,
  getDoc,
  collection,
  query,
  orderBy,
  limit,
  limitToLast,
  startAfter,
  endBefore,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaBug,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUpload,
  FaList,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

const sanitiseEmail = (email) => {
  const sanitized = email.trim().toLowerCase();
  return sanitized;
};

// Admin UIDs who can delete entries
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];

// Enhanced email validation
const validateEmail = (email) => {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!email || typeof email !== "string") {
    return { valid: false, error: "Email is required" };
  }

  const trimmed = email.trim();
  if (!trimmed) {
    return { valid: false, error: "Email cannot be empty" };
  }

  if (trimmed.length > 254) {
    return { valid: false, error: "Email is too long (max 254 characters)" };
  }

  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: "Invalid email format" };
  }

  return { valid: true, email: trimmed.toLowerCase() };
};

const BlacklistChecker = ({
  teamId,
  selectedTeam,
  teams,
  onTeamChange,
  debugEnabled = false,
  onDebugToggle,
}) => {
  const { user } = useContext(AuthContext);

  // User permissions
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);
  const canAdd = !!user; // Any authenticated user can add (creators)
  const canDelete = isAdmin; // Only admins can delete
  const canEdit = !!user; // Any authenticated user can edit

  // State for the actual checker functionality
  const [emailsText, setEmails] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("checker"); // "checker", "manage", "import"

  // Manage blacklist state
  const PAGE_SIZE = 10;
  const [manageLoading, setManageLoading] = useState(false);
  const [listEmails, setListEmails] = useState([]); // [{id,email}]
  const [firstKey, setFirstKey] = useState(null); // first email value
  const [lastKey, setLastKey] = useState(null); // last email value
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addEmailError, setAddEmailError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvHasHeader, setCsvHasHeader] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    skipped: 0,
    errors: 0,
  });

  // Conditional logging helper
  const debugLog = (...args) => {
    if (debugEnabled) {
      console.log("[BlacklistChecker]", ...args);
    }
  };

  const debugError = (...args) => {
    if (debugEnabled) {
      console.error("[BlacklistChecker]", ...args);
    }
  };

  const debugLogWithEmail = (email, sanitized) => {
    if (debugEnabled) {
      console.log(`DEBUG - Sanitizing: "${email}" → "${sanitized}"`);
    }
  };

  // Check emails against blacklist
  const checkEmails = async () => {
    if (!user) {
      return toast.error("You must be logged in to use this feature");
    }

    if (!teamId) {
      return toast.error("No team selected");
    }

    let list = emailsText
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (list.length === 0) {
      return toast.warn("Enter at least one e-mail");
    }
    if (list.length > 50) {
      return toast.error("Max 50 emails at once");
    }

    debugLog("=== BLACKLIST CHECK START ===");
    debugLog("Team ID:", teamId);
    debugLog("Selected team:", selectedTeam);
    debugLog("User:", { uid: user.uid, email: user.email });

    setLoading(true);
    try {
      const out = [];

      // Sequential fetch for blacklist docs
      for (const raw of list) {
        const id = sanitiseEmail(raw);

        // Hardcoded rule: Block all *.corteva.com emails for specific team
        if (
          teamId === "zTS5IWYUGpICLRKioMPm" &&
          raw.toLowerCase().endsWith("@corteva.com")
        ) {
          debugLog(
            `HARDCODED RULE: Blocking corteva.com email "${raw}" for team ${teamId}`
          );
          out.push({
            email: raw,
            found: true, // Mark as blacklisted
          });
          continue;
        }

        const ref = doc(db, "teams", teamId, "blacklist", id);

        debugLogWithEmail(raw, id);
        debugLog(`Document path: "teams/${teamId}/blacklist/${id}"`);

        try {
          const snap = await getDoc(ref);
          debugLog(`Document exists: ${snap.exists()}`);
          out.push({
            email: raw,
            found: snap.exists(),
          });
        } catch (err) {
          debugError(`Error checking "${id}":`, err);
          out.push({
            email: raw,
            error: err.message,
          });
        }
      }

      setResults(out);
      debugLog("Check complete:", out);
    } catch (err) {
      debugError("Global error:", err);
      toast.error("Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------
  // Manage Blacklist - CRUD + CSV
  // -------------------------------
  const collRef = teamId ? collection(db, "teams", teamId, "blacklist") : null;

  const loadPage = async (opts = {}) => {
    if (!user || !teamId) return;
    setManageLoading(true);
    try {
      let q = null;
      if (opts.direction === "next" && lastKey) {
        q = query(
          collRef,
          orderBy("email"),
          startAfter(lastKey),
          limit(PAGE_SIZE)
        );
      } else if (opts.direction === "prev" && firstKey) {
        q = query(
          collRef,
          orderBy("email"),
          endBefore(firstKey),
          limitToLast(PAGE_SIZE)
        );
      } else {
        q = query(collRef, orderBy("email"), limit(PAGE_SIZE));
      }

      const snap = await getDocs(q);
      const items = snap.docs.map((d) => {
        const data = d.data() || {};
        const email = data.email || d.id;
        return { id: d.id, email, ...data };
      });
      setListEmails(items);
      setFirstKey(items.length ? items[0].email : null);
      setLastKey(items.length ? items[items.length - 1].email : null);

      // Determine hasNext by peeking one beyond
      if (items.length === PAGE_SIZE) {
        const peekQ = query(
          collRef,
          orderBy("email"),
          startAfter(items[items.length - 1].email),
          limit(1)
        );
        const peek = await getDocs(peekQ);
        setHasNext(!peek.empty);
      } else {
        setHasNext(false);
      }

      // Determine hasPrev by peeking backwards
      if (items.length) {
        const peekPrevQ = query(
          collRef,
          orderBy("email"),
          endBefore(items[0].email),
          limitToLast(1)
        );
        const peekPrev = await getDocs(peekPrevQ);
        setHasPrev(!peekPrev.empty);
      } else {
        setHasPrev(false);
      }
    } catch (err) {
      toast.error(`Failed to load blacklist: ${err.message}`);
    } finally {
      setManageLoading(false);
    }
  };

  useEffect(() => {
    if (teamId) {
      loadPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const addSingleEmail = async () => {
    if (!canAdd) {
      return toast.error("You don't have permission to add emails");
    }

    if (!teamId) {
      return toast.error("No team selected");
    }

    const validation = validateEmail(addEmail);
    if (!validation.valid) {
      setAddEmailError(validation.error);
      return;
    }

    setAddEmailError("");
    const email = sanitiseEmail(validation.email);

    try {
      await setDoc(
        doc(db, "teams", teamId, "blacklist", email),
        {
          email,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          source: "manual",
          createdBy: user?.uid || null,
        },
        { merge: true }
      );
      toast.success("✓ Email added to blacklist");
      setAddEmail("");
      loadPage();
    } catch (err) {
      toast.error(`Failed to add: ${err.message}`);
    }
  };

  const handleAddEmailChange = (value) => {
    setAddEmail(value);
    setAddEmailError("");
  };

  const startEdit = (id, email) => {
    if (!canEdit) {
      return toast.error("You don't have permission to edit emails");
    }
    setEditingId(id);
    setEditingValue(email || id);
    setEditingError("");
  };

  const saveEdit = async () => {
    if (!canEdit) {
      return toast.error("You don't have permission to edit emails");
    }

    const validation = validateEmail(editingValue);
    if (!validation.valid) {
      setEditingError(validation.error);
      return;
    }

    const newEmail = sanitiseEmail(validation.email);
    if (newEmail === editingId) {
      setEditingId(null);
      setEditingValue("");
      setEditingError("");
      return;
    }

    try {
      // create new, then delete old
      await setDoc(
        doc(db, "teams", teamId, "blacklist", newEmail),
        {
          email: newEmail,
          updatedAt: serverTimestamp(),
          source: "edit",
          updatedBy: user?.uid || null,
        },
        { merge: true }
      );
      await deleteDoc(doc(db, "teams", teamId, "blacklist", editingId));
      toast.success("✓ Email updated");
      setEditingId(null);
      setEditingValue("");
      setEditingError("");
      loadPage();
    } catch (err) {
      toast.error(`Failed to update: ${err.message}`);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
    setEditingError("");
  };

  const handleEditingChange = (value) => {
    setEditingValue(value);
    setEditingError("");
  };

  const confirmDelete = async (id) => {
    if (!canDelete) {
      return toast.error("Only administrators can delete emails");
    }

    if (
      !window.confirm(
        `Remove ${id} from blacklist?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, "teams", teamId, "blacklist", id));
      toast.success("✓ Email removed from blacklist");
      loadPage();
    } catch (err) {
      toast.error(`Failed to delete: ${err.message}`);
    }
  };

  const handleCSVFile = (e) => {
    setCsvFile(e.target.files?.[0] || null);
  };

  const chunk = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size)
      chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  const importCSV = async () => {
    if (!csvFile) return toast.warn("Choose a CSV file");
    if (!teamId) return toast.error("No team selected");

    setImporting(true);
    setImportProgress({
      total: 0,
      processed: 0,
      success: 0,
      skipped: 0,
      errors: 0,
    });

    try {
      const parsed = await new Promise((resolve, reject) => {
        Papa.parse(csvFile, {
          complete: resolve,
          error: reject,
          skipEmptyLines: true,
        });
      });

      let rows = parsed?.data || [];
      if (csvHasHeader && rows.length) rows = rows.slice(1);
      // Flatten rows and take first cell only
      rows = rows.map((r) => (Array.isArray(r) ? r[0] : r)).filter(Boolean);
      if (rows.length === 0) {
        setImporting(false);
        return toast.warn("No rows detected");
      }
      if (rows.length > 1000) {
        rows = rows.slice(0, 1000);
        toast.info("Limiting to first 1000 rows");
      }

      const validatedEmails = rows
        .map((x) => validateEmail(String(x)))
        .filter((v) => v.valid)
        .map((v) => sanitiseEmail(v.email));

      const emails = Array.from(new Set(validatedEmails));

      setImportProgress((p) => ({ ...p, total: emails.length }));

      // Firestore batch limit 500; use 450 to be safe
      const batches = chunk(emails, 450);
      let processed = 0;
      let success = 0;
      for (const group of batches) {
        const ops = group.map((email) =>
          setDoc(
            doc(db, "teams", teamId, "blacklist", email),
            {
              email,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              source: "csv",
              createdBy: user?.uid || null,
            },
            { merge: true }
          )
        );
        await Promise.all(ops);
        processed += group.length;
        success += group.length;
        setImportProgress((p) => ({ ...p, processed, success }));
      }

      toast.success(`Imported ${success} email(s)`);
      loadPage();
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg font-medium transition-colors ${
        isActive
          ? "bg-white text-purple-600 border-b-2 border-purple-600"
          : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-900">
            Blacklist Checker
          </h1>
          {selectedTeam && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Team:</span>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                {selectedTeam.name}
              </span>
            </div>
          )}
          {isAdmin && (
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
              Admin
            </span>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Team selector for multiple teams */}
          {teams && teams.length > 1 && (
            <button
              onClick={onTeamChange}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded border"
            >
              Change Team
            </button>
          )}

          {/* Debug Logging Toggle */}
          {onDebugToggle && (
            <div className="flex items-center space-x-2">
              <FaBug
                className={`${
                  debugEnabled ? "text-green-600" : "text-gray-400"
                }`}
              />
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={debugEnabled}
                  onChange={(e) => onDebugToggle(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-purple-600"
                />
                <span className="text-sm text-gray-700">Debug Logging</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <TabButton
            id="checker"
            label="Check Emails"
            icon={FaSearch}
            isActive={activeTab === "checker"}
            onClick={setActiveTab}
          />
          <TabButton
            id="manage"
            label="Manage List"
            icon={FaList}
            isActive={activeTab === "manage"}
            onClick={setActiveTab}
          />
          <TabButton
            id="import"
            label="Bulk Import"
            icon={FaUpload}
            isActive={activeTab === "import"}
            onClick={setActiveTab}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Check Emails Tab */}
        {activeTab === "checker" && (
          <div className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Addresses to Check
              </label>
              <textarea
                value={emailsText}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Paste up to 50 email addresses – one per line, comma or space-separated"
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y"
              />
              <p className="text-sm text-gray-500 mt-1">
                Supports multiple formats: line-separated, comma-separated, or
                space-separated
              </p>
            </div>

            <button
              onClick={checkEmails}
              disabled={loading || !teamId}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaSearch className="mr-2" />
              {loading ? "Checking..." : "Check Emails"}
            </button>

            {/* Results */}
            {results.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Check Results</h3>
                <div className="overflow-hidden border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {results.map(({ email, found, error }) => (
                        <tr
                          key={email}
                          className={
                            error
                              ? "bg-yellow-50"
                              : found
                              ? "bg-red-50"
                              : "bg-green-50"
                          }
                        >
                          <td className="px-6 py-4 font-mono text-sm break-all">
                            {email}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              {error ? (
                                <>
                                  <FaExclamationTriangle className="text-yellow-600" />
                                  <span className="text-yellow-600 font-medium text-sm">
                                    Error: {error}
                                  </span>
                                </>
                              ) : found ? (
                                <>
                                  <FaTimesCircle className="text-red-600" />
                                  <span className="text-red-600 font-medium">
                                    ⚠️ Blacklisted - Do NOT Use
                                  </span>
                                </>
                              ) : (
                                <>
                                  <FaCheckCircle className="text-green-600" />
                                  <span className="text-green-600 font-medium">
                                    ✓ Safe to Use
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manage List Tab */}
        {activeTab === "manage" && (
          <div className="p-6">
            {/* Add Email Section */}
            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaPlus className="mr-2 text-blue-600" />
                Add Email to Blacklist
              </h3>
              <div className="flex flex-col space-y-3">
                <div className="flex space-x-3">
                  <input
                    type="email"
                    value={addEmail}
                    onChange={(e) => handleAddEmailChange(e.target.value)}
                    placeholder="Enter email address to blacklist"
                    className={`flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      addEmailError ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                  <button
                    onClick={addSingleEmail}
                    disabled={!canAdd || !teamId || !addEmail.trim()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                  >
                    <FaPlus className="mr-2" />
                    Add
                  </button>
                </div>
                {addEmailError && (
                  <p className="text-red-600 text-sm flex items-center">
                    <FaExclamationTriangle className="mr-1" />
                    {addEmailError}
                  </p>
                )}
                {!canAdd && (
                  <p className="text-gray-500 text-sm">
                    You need to be logged in to add emails to the blacklist.
                  </p>
                )}
              </div>
            </div>

            {/* Email List */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">
                Current Blacklist ({listEmails.length} entries)
              </h3>
              <div className="overflow-hidden border rounded-lg">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email Address
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {manageLoading && (
                      <tr>
                        <td
                          className="px-6 py-8 text-center text-gray-500"
                          colSpan={2}
                        >
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mr-2"></div>
                            Loading blacklist...
                          </div>
                        </td>
                      </tr>
                    )}
                    {!manageLoading && listEmails.length === 0 && (
                      <tr>
                        <td
                          className="px-6 py-8 text-center text-gray-500"
                          colSpan={2}
                        >
                          No emails in blacklist yet
                        </td>
                      </tr>
                    )}
                    {listEmails.map(({ id, email }) => (
                      <tr key={id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          {editingId === id ? (
                            <div className="space-y-2">
                              <input
                                className={`w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                                  editingError
                                    ? "border-red-300"
                                    : "border-gray-300"
                                }`}
                                value={editingValue}
                                onChange={(e) =>
                                  handleEditingChange(e.target.value)
                                }
                              />
                              {editingError && (
                                <p className="text-red-600 text-sm flex items-center">
                                  <FaExclamationTriangle className="mr-1" />
                                  {editingError}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="font-mono text-sm">
                              {email || id}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-2">
                            {editingId === id ? (
                              <>
                                <button
                                  onClick={saveEdit}
                                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 flex items-center text-sm"
                                >
                                  <FaCheck className="mr-1" />
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 flex items-center text-sm"
                                >
                                  <FaTimes className="mr-1" />
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                {canEdit && (
                                  <button
                                    onClick={() => startEdit(id, email)}
                                    className="bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200 flex items-center text-sm"
                                  >
                                    <FaEdit className="mr-1" />
                                    Edit
                                  </button>
                                )}
                                {canDelete ? (
                                  <button
                                    onClick={() => confirmDelete(id)}
                                    className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 flex items-center text-sm"
                                  >
                                    <FaTrash className="mr-1" />
                                    Delete
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    Admin only
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => loadPage({ direction: "prev" })}
                  disabled={!hasPrev || manageLoading}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Previous 10
                </button>
                <span className="text-sm text-gray-600">
                  Showing up to {PAGE_SIZE} entries per page
                </span>
                <button
                  onClick={() => loadPage({ direction: "next" })}
                  disabled={!hasNext || manageLoading}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next 10 →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Import Tab */}
        {activeTab === "import" && (
          <div className="p-6">
            <div className="max-w-2xl">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FaUpload className="mr-2 text-purple-600" />
                Bulk Import via CSV
              </h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Choose CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVFile}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="csvHeader"
                    checked={csvHasHeader}
                    onChange={(e) => setCsvHasHeader(e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="csvHeader" className="text-sm text-gray-700">
                    First row contains header
                  </label>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">
                    CSV Format Requirements:
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>
                      • CSV must contain email addresses in the first column
                    </li>
                    <li>• Maximum 1000 rows per upload (extra rows ignored)</li>
                    <li>
                      • Duplicate and invalid emails are automatically skipped
                    </li>
                    <li>• Only authenticated users can import emails</li>
                  </ul>
                </div>

                <button
                  onClick={importCSV}
                  disabled={!csvFile || importing || !canAdd}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                >
                  <FaUpload className="mr-2" />
                  {importing ? "Importing..." : "Import CSV"}
                </button>

                {!canAdd && (
                  <p className="text-gray-500 text-sm">
                    You need to be logged in to import emails.
                  </p>
                )}

                {importing && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                      <span className="text-sm font-medium">
                        Importing emails...
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        Progress: {importProgress.processed}/
                        {importProgress.total} processed
                      </div>
                      <div>
                        ✓ {importProgress.success} successfully imported
                      </div>
                      {importProgress.errors > 0 && (
                        <div>⚠️ {importProgress.errors} errors</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlacklistChecker;
